import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, ThumbsUp, ThumbsDown, Loader2, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTheme } from '../../contexts/ThemeContext';
import { getWebOrigin } from '../../utils/helpers';
import { renderChatMessage } from '../../utils/renderChatMessage';

const HELP_API = getWebOrigin() + '/helpapi';
const HELP_APP_ID = import.meta.env.REACT_APP_HELP_APP_ID;
const HIDDEN_KEY = 'helpchat_hidden';

export default function HelpChat() {
  const { theme } = useTheme();
  const [hidden, setHidden] = useState(() => localStorage.getItem(HIDDEN_KEY) === 'true');
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 'welcome',
      role: 'assistant',
      content: "Hi! I'm Kyna, your Kynaix Sports assistant. Ask me anything about tournaments, teams, scoring, or any other feature.",
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [rated, setRated] = useState({});
  const bottomRef = useRef(null);
  const textareaRef = useRef(null);
  const inputHistory = useRef([]);   // sent messages
  const historyIndex = useRef(-1);   // -1 = not browsing
  const draft = useRef('');          // saved draft while browsing

  useEffect(() => {
    if (open) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, open]);

  // Allow the Help page's "Open Chat" button to open this widget
  useEffect(() => {
    const handler = () => {
      localStorage.removeItem(HIDDEN_KEY);
      setHidden(false);
      setOpen(true);
    };
    window.addEventListener('open-help-chat', handler);
    return () => window.removeEventListener('open-help-chat', handler);
  }, []);

  // Allow Header's user menu to re-show the widget
  useEffect(() => {
    const handler = () => {
      localStorage.removeItem(HIDDEN_KEY);
      setHidden(false);
      setOpen(true);
    };
    window.addEventListener('show-help-chat', handler);
    return () => window.removeEventListener('show-help-chat', handler);
  }, []);

  const handleHide = () => {
    setOpen(false);
    setHidden(true);
    localStorage.setItem(HIDDEN_KEY, 'true');
    toast('Chat hidden. To reopen, go to your profile menu → Kyna.', {
      duration: 5000,
      icon: '💬',
      style: { maxWidth: '320px', fontSize: '13px' },
    });
  };

  const getHistory = () =>
    messages
      .filter((m) => m.id !== 'welcome')
      .map((m) => ({ role: m.role, content: m.content }));

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    inputHistory.current.push(text);
    historyIndex.current = -1;
    draft.current = '';

    const userMsg = { id: Date.now().toString(), role: 'user', content: text };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${HELP_API}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          history: getHistory(),
          current_page: window.location.pathname,
          user_token: token || undefined,
          app_id: HELP_APP_ID,
        }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      setMessages((prev) => [
        ...prev,
        { id: `a-${Date.now()}`, role: 'assistant', content: data.reply },
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          id: `err-${Date.now()}`,
          role: 'assistant',
          content: 'Sorry, something went wrong. Please try again.',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleRate = async (msgId, msgContent, rating) => {
    if (rated[msgId]) return;
    setRated((prev) => ({ ...prev, [msgId]: rating }));
    try {
      await fetch(`${HELP_API}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rating,
          message_content: msgContent,
          conversation_id: messages[0]?.id || '',
          app_id: HELP_APP_ID,
        }),
      });
    } catch (_) {
      // silent — feedback is best-effort
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
      return;
    }

    if (e.key === 'ArrowUp') {
      const history = inputHistory.current;
      if (history.length === 0) return;
      e.preventDefault();
      if (historyIndex.current === -1) {
        draft.current = input;
        historyIndex.current = history.length - 1;
      } else if (historyIndex.current > 0) {
        historyIndex.current -= 1;
      }
      const val = history[historyIndex.current];
      setInput(val);
      setTimeout(() => {
        const el = textareaRef.current;
        if (el) { el.selectionStart = el.selectionEnd = val.length; }
      }, 0);
      return;
    }

    if (e.key === 'ArrowDown') {
      if (historyIndex.current === -1) return;
      e.preventDefault();
      const history = inputHistory.current;
      if (historyIndex.current < history.length - 1) {
        historyIndex.current += 1;
        const val = history[historyIndex.current];
        setInput(val);
        setTimeout(() => {
          const el = textareaRef.current;
          if (el) { el.selectionStart = el.selectionEnd = val.length; }
        }, 0);
      } else {
        historyIndex.current = -1;
        setInput(draft.current);
      }
    }
  };

  if (hidden) return null;

  return (
    <>
      {/* Floating bubble */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-20 right-4 md:bottom-6 md:right-6 z-50 w-14 h-14 rounded-full bg-orange-500 hover:bg-orange-400 active:bg-orange-600 text-white shadow-lg flex items-center justify-center transition-colors"
        aria-label="Toggle Kyna"
      >
        {open ? <X size={24} /> : <MessageCircle size={24} />}
      </button>

      {/* Chat panel */}
      {open && (
        <div
          className={`fixed bottom-36 right-4 md:bottom-24 md:right-6 z-50 w-80 sm:w-96 flex flex-col rounded-2xl shadow-2xl overflow-hidden border ${theme.card}`}
          style={{ maxHeight: '70vh' }}
        >
          {/* Header */}
          <div className={`flex items-center gap-3 px-4 py-3 flex-shrink-0 border-b ${theme.backgroundSecondary} ${theme.border}`}>
            <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
              KY
            </div>
            <div className="flex-1 min-w-0">
              <p className={`${theme.text} text-sm font-semibold leading-tight`}>Kyna</p>
              <p className={`${theme.textSecondary} text-xs`}>Ask me anything</p>
            </div>
            <button
              onClick={handleHide}
              className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs ${theme.textMuted} ${theme.cardHover} transition-colors flex-shrink-0`}
              title="Hide chat"
            >
              <EyeOff size={13} />
              Hide
            </button>
          </div>

          {/* Messages */}
          <div className={`flex-1 overflow-y-auto px-4 py-3 space-y-3 ${theme.background}`}>
            {messages.map((msg) => (
              <div key={msg.id} className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-3 py-2 text-base whitespace-pre-wrap break-words ${
                    msg.role === 'user'
                      ? 'bg-orange-500 text-white rounded-br-sm'
                      : `${theme.backgroundSecondary} ${theme.text} rounded-bl-sm`
                  }`}
                >
                  {renderChatMessage(msg.content)}
                </div>

                {/* Thumbs for assistant messages (not welcome) */}
                {msg.role === 'assistant' && msg.id !== 'welcome' && (
                  <div className="flex gap-2 mt-1">
                    <button
                      onClick={() => handleRate(msg.id, msg.content, 'positive')}
                      className={`p-1 rounded transition-colors ${
                        rated[msg.id] === 'positive' ? 'text-green-400' : `${theme.textMuted} hover:text-green-400`
                      }`}
                      aria-label="Thumbs up"
                    >
                      <ThumbsUp size={13} />
                    </button>
                    <button
                      onClick={() => handleRate(msg.id, msg.content, 'negative')}
                      className={`p-1 rounded transition-colors ${
                        rated[msg.id] === 'negative' ? 'text-red-400' : `${theme.textMuted} hover:text-red-400`
                      }`}
                      aria-label="Thumbs down"
                    >
                      <ThumbsDown size={13} />
                    </button>
                  </div>
                )}
              </div>
            ))}

            {loading && (
              <div className="flex items-start">
                <div className={`${theme.backgroundSecondary} rounded-2xl rounded-bl-sm px-3 py-2`}>
                  <Loader2 size={16} className="text-orange-400 animate-spin" />
                </div>
              </div>
            )}

            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className={`flex items-end gap-2 px-3 py-3 flex-shrink-0 border-t ${theme.backgroundSecondary} ${theme.border}`}>
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a message..."
              rows={1}
              className={`flex-1 text-base rounded-xl px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-orange-500 border ${theme.input}`}
              style={{ maxHeight: '100px', overflowY: 'auto' }}
            />
            <button
              onClick={sendMessage}
              disabled={!input.trim() || loading}
              className="w-9 h-9 flex items-center justify-center rounded-xl bg-orange-500 hover:bg-orange-400 active:bg-orange-600 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex-shrink-0"
              aria-label="Send"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
